import { inject, type ObjectGraph } from '@jet/environment/dependencies';
import type { Opt } from '@jet/environment';
import { getBaseSeoData } from './BaseSeoData';
import type { SeoData } from './types';
import type { DetailPage } from '@jet-app/music-classical/models/pages/detail';
import { ObjectGraphType } from '@jet-app/music-classical/dependencies';
import type { CropCode } from './types';

const PARAGRAPH_TEXT_LIMIT = 160;

/**
 * @method getMultiRoomSeoData
 * @param {string} url Canonical URL
 * @param {DetailPage} page Page object
 * @param {ObjectGraph} objectGraph
 * @returns {SeoData}
 */
export function getMultiRoomSeoData(
    url: string,
    page: DetailPage,
    objectGraph: ObjectGraph,
    id?: string,
): SeoData {
    const localization = inject(ObjectGraphType.localization, objectGraph);
    const baseSeoData: SeoData = getBaseSeoData(url, objectGraph);
    const multiroomName = page.header.title;
    const artworkObj = page.header.artwork;
    const crop: CropCode = 'cw';
    const siteName: string = localization.string(
        'ASE.Web.MusicClassicalClientJS.Name',
    );

    function getPageTitle() {
        if (multiroomName) {
            return localization.string(
                'ASE.Web.MusicClassicalClientJS.ContentA.Dash.ContentB',
                { contentA: multiroomName, contentB: siteName },
            );
        }

        return baseSeoData.pageTitle;
    }

    function getPageDescription() {
        const firstTextSection =
            page.sections[0].itemKind === 'paragraph' && page.sections[0];

        if (!!firstTextSection) {
            if (firstTextSection.text.length <= PARAGRAPH_TEXT_LIMIT) {
                return firstTextSection.text;
            }

            return localization.string(
                'ASE.Web.MusicClassicalClientJS.TruncatedContent',
                {
                    content: firstTextSection.text.slice(
                        0,
                        PARAGRAPH_TEXT_LIMIT - 3,
                    ),
                },
            );
        }

        return baseSeoData.description;
    }

    const pageTitle: string = getPageTitle();
    const description: Opt<string> = getPageDescription();

    /**
     *
     * Generate SEO data object
     *
     */
    return {
        ...baseSeoData,
        pageTitle,
        description,

        /**
         * Social OG Tags
         */
        socialTitle: pageTitle,
        socialDescription: description,

        /**
         *
         * Apple-specific Values
         *
         */
        appleContentId: id,
        appleTitle: pageTitle,
        appleDescription: description,

        /**
         *
         * Artwork Values
         *
         */
        crop,
        twitterCropCode: crop,
        artworkUrl: artworkObj?.template ?? baseSeoData.artworkUrl,
    };
}

export function addingMultiRoomSeoData(
    url: string,
    page: DetailPage,
    objectGraph: ObjectGraph,
    id?: string,
): DetailPage {
    return {
        ...page,
        seoData: getMultiRoomSeoData(url, page, objectGraph, id),
    };
}
